import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import {
  Competition,
  CompetitionPicks,
  UserPick,
  CompetitionContextState,
  GameState,
} from '@/types/competition';
import { useUser } from './UserContext';

const CompetitionContext = createContext<CompetitionContextState | undefined>(undefined);

interface CompetitionProviderProps {
  children: ReactNode;
}

export const CompetitionProvider: React.FC<CompetitionProviderProps> = ({ children }) => {
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [userPicks, setUserPicks] = useState<Record<string, CompetitionPicks>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user, isAuthenticated } = useUser();

  /**
   * Get a specific competition by ID
   */
  const getCompetition = useCallback(
    (id: string): Competition | undefined => {
      return competitions.find(comp => comp.id === id);
    },
    [competitions]
  );

  /**
   * Get upcoming competitions
   */
  const getUpcomingCompetitions = useCallback((): Competition[] => {
    return competitions.filter(comp => comp.state === 'upcoming');
  }, [competitions]);

  /**
   * Get active competitions (countdown or live)
   */
  const getActiveCompetitions = useCallback((): Competition[] => {
    return competitions.filter(comp => ['countdown', 'live'].includes(comp.state));
  }, [competitions]);

  /**
   * Get completed competitions
   */
  const getCompletedCompetitions = useCallback((): Competition[] => {
    return competitions.filter(comp => comp.state === 'final');
  }, [competitions]);

  /**
   * Submit picks for a competition
   */
  const submitPicks = useCallback(
    async (competitionId: string, picks: UserPick[], tieBreakerAnswer?: number) => {
      if (!user) {
        throw new Error('Must be logged in to submit picks');
      }

      setIsLoading(true);
      setError(null);

      try {
        // TODO: Implement real submit picks API call
        // await apiClient.post(`/pickems/${competitionId}/submit`, { picks, tieBreakerAnswer });

        // Store picks locally
        const competitionPicks: CompetitionPicks = {
          competitionId,
          userId: user.id,
          picks,
          tieBreakerAnswer,
          submittedAt: new Date(),
        };

        setUserPicks(prev => ({
          ...prev,
          [competitionId]: competitionPicks,
        }));
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to submit picks';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [user]
  );

  /**
   * Get user's picks for a competition
   */
  const getUserPicks = useCallback(
    (competitionId: string): CompetitionPicks | undefined => {
      return userPicks[competitionId];
    },
    [userPicks]
  );

  /**
   * Refresh competitions from API
   */
  const refreshCompetitions = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // TODO: Implement real get competitions API call
      // const data = await apiClient.get('/pickems');
      // setCompetitions(data);

      // For now, return empty array
      setCompetitions([]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch competitions';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Initialize competitions on mount and when auth changes
   */
  useEffect(() => {
    const initCompetitions = async () => {
      // Generate mock data for now
      const now = new Date();
      const mockCompetitions: Competition[] = [
        {
          id: 'game1',
          title: 'SAT FEB 01',
          prize: '1st $100 Gift Card',
          state: 'upcoming' as GameState,
          gameDate: new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000),
          questions: [
            {
              id: 'prop1',
              type: 'prop',
              teams: ['devils', 'penguins'],
              question: '6+ goals',
              points: '--/5pts',
              percentages: { yes: 50, no: 50 },
            },
            {
              id: 'team1',
              type: 'team',
              teams: ['oilers', 'panthers'],
              points: '--/5pts',
            },
            {
              id: 'prop2',
              type: 'prop',
              teams: ['devils', 'penguins'],
              question: '6+ goals',
              points: '--/5pts',
              percentages: { yes: 50, no: 50 },
            },
            {
              id: 'team2',
              type: 'team',
              teams: ['oilers', 'panthers'],
              points: '--/5pts',
            },
          ],
          tieBreaker: {
            description: 'Predict the amount of shots combined for Bruins and Golden Knights',
            teams: ['devils', 'penguins'],
          },
        },
        {
          id: 'game2',
          title: 'SAT FEB 01',
          prize: '1st $100 Gift Card',
          state: 'countdown' as GameState,
          gameDate: new Date(now.getTime() + 42 * 60 * 1000 + 7 * 1000),
          questions: [],
          tieBreaker: {
            description: 'Predict the amount of shots combined for teams',
            teams: ['oilers', 'panthers'],
          },
        },
        {
          id: 'game3',
          title: 'SAT FEB 01',
          prize: '1st $100 Gift Card',
          state: 'live' as GameState,
          gameDate: new Date(now.getTime() - 30 * 60 * 1000),
          questions: [],
          tieBreaker: {
            description: 'Predict the amount of shots combined for teams',
            teams: ['devils', 'penguins'],
          },
        },
        {
          id: 'game4',
          title: 'SAT FEB 01',
          prize: '1st $100 Gift Card',
          state: 'final' as GameState,
          gameDate: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000),
          finalScore: { rank: 68, points: 12 },
          questions: [],
          tieBreaker: {
            description: 'Predict the amount of shots combined for teams',
            teams: ['oilers', 'panthers'],
          },
        },
      ];

      setCompetitions(mockCompetitions);

      // If authenticated, fetch user's picks
      if (isAuthenticated && user) {
        // Mock user picks for demo
        setUserPicks({
          game4: {
            competitionId: 'game4',
            userId: user.id,
            picks: [
              { questionId: 'q1', answer: 'team1', points: 5 },
              { questionId: 'q2', answer: 'yes', points: 3 },
            ],
            tieBreakerAnswer: 42,
            submittedAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000),
            totalPoints: 12,
          },
        });
      }
    };

    initCompetitions();
  }, [isAuthenticated, user]);

  const value: CompetitionContextState = {
    competitions,
    userPicks,
    isLoading,
    error,
    getCompetition,
    getUpcomingCompetitions,
    getActiveCompetitions,
    getCompletedCompetitions,
    submitPicks,
    getUserPicks,
    refreshCompetitions,
  };

  return <CompetitionContext.Provider value={value}>{children}</CompetitionContext.Provider>;
};

/**
 * Hook to use CompetitionContext
 */
export const useCompetitions = () => {
  const context = useContext(CompetitionContext);
  if (context === undefined) {
    throw new Error('useCompetitions must be used within a CompetitionProvider');
  }
  return context;
};
